import { supabase } from '@/shared/lib/supabase';

/**
 * adminAccountsService — cross-customer hosting reads.
 *
 * Reads `v_admin_hosting_accounts` (security_invoker view that joins
 * customer_link + customer_profile + auth.users.email + service_plan
 * server-side). Live WHM state is intentionally NOT joined here — the
 * per-customer Hosting tab calls `whm-account-summary` for that;
 * cross-customer rolls of WHM state would burn N WHM API calls per
 * page load. The list shows portal-side state only; click into a
 * customer for live data.
 */

export type AdminAccountRow = {
  id: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  cpanel_username: string;
  whm_server: string;
  primary_domain: string | null;
  package_name: string | null;
  service_plan_name: string | null;
  service_plan_amount: number | null;
  billing_cycle: string | null;
  linked_at: string;
  unlinked_at: string | null;
  current_period_end: string | null;
  scheduled_termination_at: string | null;
  /** `standard` | `no_cancel` | `no_suspend`. Decides whether billing-cron may
   *  suspend or terminate this account — see domains/hosting/dunningPolicy.ts. */
  dunning_policy: string | null;
  /** Slice 2 (2026-09-10): OBSERVED state — what WHM last told us. */
  hosting_state: string | null;
  hosting_state_observed_at: string | null;
  /** Slice 2: DESIRED state — what the books say it should be, and why. */
  hosting_desired_state: string | null;
  hosting_desired_reason_code: string | null;
  /** Slice 2: agree | converging | books_suspended_server_live | server_suspended_books_active | unverified */
  books_vs_server: string | null;
  /**
   * Latest daily `usage_snapshot` for this link. `null` means no snapshot row,
   * which is a real and different thing from 0 MB used — the account may be
   * newer than the last cron run, or the runner may have skipped it. Render the
   * two differently; do not coalesce to zero.
   */
  disk_used_mb: number | null;
  disk_quota_mb: number | null;
  usage_as_of: string | null;
};

export type WhmServerHealthRow = {
  hostname: string;
  port: number;
  api_user: string;
  is_active: boolean;
  capacity_max_accounts: number | null;
  active_accounts: number;
  spare_capacity: number | null;
  failures_24h: number;
  last_health_check_at: string | null;
};

interface ViewRow {
  id: string;
  customer_id: string;
  customer_name: string | null;
  customer_email: string | null;
  cpanel_username: string;
  whm_server: string;
  primary_domain: string | null;
  package_name: string | null;
  service_plan_name: string | null;
  service_plan_amount: string | number | null;
  billing_cycle: string | null;
  linked_at: string;
  unlinked_at: string | null;
  current_period_end: string | null;
  scheduled_termination_at: string | null;
  dunning_policy: string | null;
  hosting_state: string | null;
  hosting_state_observed_at: string | null;
  hosting_desired_state: string | null;
  hosting_desired_reason_code: string | null;
  books_vs_server: string | null;
}

export const adminAccountsService = {
  async listAll(includeUnlinked = false): Promise<AdminAccountRow[]> {
    let query = supabase
      .from('v_admin_hosting_accounts')
      .select(
        'id, customer_id, customer_name, customer_email, cpanel_username, whm_server, primary_domain, package_name, service_plan_name, service_plan_amount, billing_cycle, linked_at, unlinked_at, current_period_end, scheduled_termination_at, dunning_policy, hosting_state, hosting_state_observed_at, hosting_desired_state, hosting_desired_reason_code, books_vs_server',
      )
      .order('linked_at', { ascending: false });
    if (!includeUnlinked) query = query.is('unlinked_at', null);

    const { data, error } = await query;
    if (error) throw new Error(`Could not load accounts: ${error.message}`);

    const rows = ((data ?? []) as ViewRow[]).map((r) => ({
      ...r,
      service_plan_amount:
        r.service_plan_amount === null ? null : Number(r.service_plan_amount),
      disk_used_mb: null as number | null,
      disk_quota_mb: null as number | null,
      usage_as_of: null as string | null,
    }));
    if (rows.length === 0) return rows;

    // The Accounts list named a WHM package but never how full the account
    // actually was — the one number that says whether a plan still fits. The
    // daily `usage_snapshot` rollup has held it since 2026-05-13 and nothing
    // read it outside the server-health page.
    //
    // A failure here degrades the disk column to "—" rather than taking the
    // whole page down: the account list is useful without usage, and usage is
    // useless without the account list.
    const { data: usage, error: usageErr } = await supabase
      .from('usage_snapshot')
      .select('customer_link_id, disk_used_mb, disk_quota_mb, snapshot_date')
      .in('customer_link_id', rows.map((r) => r.id))
      .order('snapshot_date', { ascending: false });
    if (usageErr || !usage) return rows;

    // Ordered newest-first, so the first row seen per link is the latest.
    const latest = new Map<string, { used: number | null; quota: number | null; on: string }>();
    for (const u of usage as Array<{
      customer_link_id: string;
      disk_used_mb: number | null;
      disk_quota_mb: number | null;
      snapshot_date: string;
    }>) {
      if (latest.has(u.customer_link_id)) continue;
      latest.set(u.customer_link_id, {
        used: u.disk_used_mb,
        quota: u.disk_quota_mb,
        on: u.snapshot_date,
      });
    }
    for (const r of rows) {
      const u = latest.get(r.id);
      if (!u) continue;
      r.disk_used_mb = u.used;
      r.disk_quota_mb = u.quota;
      r.usage_as_of = u.on;
    }
    return rows;
  },

  async getServerHealth(): Promise<WhmServerHealthRow[]> {
    const { data, error } = await supabase
      .from('v_admin_whm_server_health')
      .select('hostname, port, api_user, is_active, capacity_max_accounts, active_accounts, spare_capacity, failures_24h, last_health_check_at');
    if (error) throw new Error(`Could not load server health: ${error.message}`);
    return (data ?? []) as WhmServerHealthRow[];
  },
};
